import type { Collider, ColliderDesc, RigidBody, World } from "@dimforge/rapier3d-compat";
import type { Vec3 } from "./body";
import type { MaterialDef, Size } from "./materials";

/** The Rapier module, passed in so the core works with any loaded build. */
export type Rapier = typeof import("@dimforge/rapier3d-compat").default;

export const STEP = 1 / 120; // small fixed step keeps stacks stable
const MAX_STEPS = 8;

// Rapier stores friction and bounciness on each collider and combines the
// values of two touching colliders with a rule. Storing the square root of
// friction and multiplying gives the geometric mean (ice makes anything
// slippery); 'Max' for bounciness means rubber bounces off anything.
function surfaceProps(
  RAPIER: Rapier,
  desc: ColliderDesc,
  friction: number,
  restitution: number,
): ColliderDesc {
  const RULE = RAPIER.CoefficientCombineRule;
  return desc
    .setFriction(Math.sqrt(friction))
    .setFrictionCombineRule(RULE.Multiply)
    .setRestitution(restitution)
    .setRestitutionCombineRule(RULE.Max);
}

/**
 * The world with Earth gravity and the base: an invisible fixed body that
 * blocks rest on (only its grid lines are drawn).
 */
export function createWorld(RAPIER: Rapier): { world: World; baseCollider: Collider } {
  const world = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
  world.timestep = STEP;
  const baseBody = world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(0, -0.75, 0));
  const baseCollider = world.createCollider(
    surfaceProps(RAPIER, RAPIER.ColliderDesc.cuboid(12, 0.75, 12), 0.6, 0.1),
    baseBody,
  );
  return { world, baseCollider };
}

/** A dynamic box of one material, turned `yaw` radians about the vertical axis. */
export function createBlockBody(
  RAPIER: Rapier,
  world: World,
  mat: MaterialDef,
  size: Size,
  pos: Vec3,
  yaw: number,
): { body: RigidBody; collider: Collider } {
  const [w, h, d] = size;
  const body = world.createRigidBody(
    RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(pos.x, pos.y, pos.z)
      .setRotation({ x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) })
      .setLinearDamping(0.05)
      .setAngularDamping(0.1)
      .setCcdEnabled(true), // continuous collision detection: fast blocks can't tunnel
  );
  // The mass comes from density × volume automatically
  const collider = world.createCollider(
    surfaceProps(
      RAPIER,
      RAPIER.ColliderDesc.cuboid(w / 2, h / 2, d / 2).setDensity(mat.density),
      mat.friction,
      mat.restitution,
    ),
    body,
  );
  return { body, collider };
}

/**
 * Fixed timestep: runs as many STEP-long steps as the elapsed time needs, at
 * most 8 per frame. Returns the time left over for the next frame.
 */
export function stepFixed(
  world: { step(): void },
  acc: number,
  dt: number,
): { acc: number; steps: number } {
  acc += dt;
  let steps = 0;
  while (acc >= STEP && steps < MAX_STEPS) {
    world.step();
    acc -= STEP;
    steps++;
  }
  if (steps === MAX_STEPS) acc = 0; // fell too far behind: drop the backlog
  return { acc, steps };
}
